import React, { useState, useEffect} from 'react';
import {
  Box, Button, Table, TableHead, TableBody, TableRow, TableCell,
  tableCellClasses, Typography, Dialog, DialogActions, DialogContent,
  DialogContentText, DialogTitle, MenuItem
} from '@mui/material';
import styled from "@emotion/styled";
import moment from 'moment';
import axios from 'axios';
import ToastAlert from '../../utils/Toaster'
import { useParams } from 'react-router-dom';
import { ShimmerSkeleton } from '../../utils/SkimmerSkeleton';
import { getErrorMessage } from '../../utils/validation'
import { maskToken } from '../../utils/validation';
import { ApiService } from '../../API/AxiosInstance';
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import DeviceTokenDesc from '../../components/Notifications/DeviceTokenDesc';
import { colors } from '../../Theme/tokens';
import { ReactComponent as AppleIcon } from '../../img/settings/deviceToken/appleIcon.svg';
import { ReactComponent as AndroidIcon } from '../../img/settings/deviceToken/androidIcon.svg';
import { ReactComponent as WebIcon } from '../../img/settings/deviceToken/webIcon.svg';
import { ReactComponent as CreatedOnIcon } from '../../img/settings/deviceToken/createdOnIcon.svg';
import { ReactComponent as TokenIcon } from '../../img/settings/deviceToken/tokenIcon.svg';


export const StyledTableRow = styled(TableRow)({});

export const StyledTableCell = styled(TableCell)({
  textAlign: 'center',
  whiteSpace: 'nowrap',
  border: 'none',
  width: 'fit-content',
  
  [`&.${tableCellClasses.head}`]: {
    fontSize: '15px',
    fontWeight: 500,
    padding: '20px 15px 7px 15px',
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: '15px',
    fontWeight: 500,
    padding: '27px 15px 27px 15px',
  },
});

// Pick the right icon + circle color per platform
const getPlatformIcon = (platform) => {
  switch (platform?.toLowerCase()) {
    case 'ios':
      return { icon: <AppleIcon  /> };
    case 'android':
      return { icon: <AndroidIcon  /> };
    case 'web':
    default:
      return { icon: <WebIcon  /> };
  }
};

const DeviceTokens = () => {
  const id= localStorage.getItem('userData') ? JSON.parse(localStorage.getItem('userData'))?.sub_id : ''

  const [deviceTokens, setDeviceTokens] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toastStatus, setToastStatus] = useState({ color: '', status: false, message: '', icon: '' });

  const [deleteModal, setDeleteModal] = useState(false);
  const [selectedToken, setSelectedToken] = useState(null);

//   const latestToken = [...deviceTokens]?.sort(
//   (a, b) => new Date(b.lastLoginAt) - new Date(a.lastLoginAt)
// )[0];

  const latestTokenByPlatform = deviceTokens.reduce((acc, token) => {
    const platform = token.platform;

    if (
      !acc[platform] ||
      new Date(token.lastLoginAt) > new Date(acc[platform].lastLoginAt)
    ) {
      acc[platform] = token;
    }

    return acc;
  }, {});



  useEffect(() => {
    getAllDeviceTokens();
  }, []);

  const getAllDeviceTokens = async () => {
    setLoading(true);
    try {
      const response = await ApiService().get(`notifications/devices/users/${id}`);
      if (response?.data?.data?.length > 0) {
        setDeviceTokens(response?.data?.data);
      } else {
        setDeviceTokens([]);
      }
    } catch (error) {
      setDeviceTokens([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (token) => {
    setSelectedToken(token);
    setDeleteModal(true);
  };

  const handleDeleteClose = () => {
    setDeleteModal(false);
    setSelectedToken(null);
  };

  const handleConfirmDelete = async () => {
    setLoading(true);
    try {
      const response = await ApiService().post(
        `notifications/devices/${selectedToken?.deviceId}/delete`,
        {
            userId: selectedToken?.userId,
            token: selectedToken?.token,
            platform: selectedToken?.platform
        }
      );
      setToastStatus({ color: 'green', status: true, message: response?.data?.message, icon: 'tick' });
      getAllDeviceTokens();
      setTimeout(() => setToastStatus({ color: '', status: false }), 3000);
    } catch (error) {
      setToastStatus({ color: 'red', status: true, message: getErrorMessage(error), icon: 'close' });
      setTimeout(() => setToastStatus({ color: '', status: false }), 3000);
    } finally {
      setLoading(false);
      handleDeleteClose();
    }
  };

  const isLatestForPlatform = (item) => latestTokenByPlatform[item.platform]?.deviceId === item.deviceId;

  if (loading) {
    return (
      <Table>
        <TableBody>
          {Array.from(new Array(5)).map((_, rowIndex) => (
            <StyledTableRow key={rowIndex}>
              {Array.from(new Array(5)).map((_, colIndex) => (
                <StyledTableCell key={colIndex}>
                  <ShimmerSkeleton variant="rectangular" width="100%" height={40} />
                </StyledTableCell>
              ))}
            </StyledTableRow>
          ))}
        </TableBody>
      </Table>
    );
  }

  return (
    <>
      <ToastAlert
        message={toastStatus?.message}
        status={toastStatus?.status}
        color={toastStatus?.color}
        icon={toastStatus?.icon}
        closeFunc={() => setToastStatus({ color: '', status: false })}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteModal} onClose={handleDeleteClose} sx={{ fontSize: "18px" }}>
        <Box sx={{ p: 2 }}>
          <DialogTitle sx={{ fontSize: "18px", p: 0 }}>Delete Token</DialogTitle>
          <DialogContent sx={{ px: 0, pt: 0, textAlign: "left" }}>
            <DialogContentText sx={{ p: 0, mt: 2 }}>
              Are you sure you want to delete this device token?
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ justifyContent: "space-between" }}>
            <Button onClick={handleDeleteClose} color="primary">
              Cancel
            </Button>
            <Button
              onClick={handleConfirmDelete}
              sx={{ backgroundColor: "red", '&:hover': { backgroundColor: "red" }, color: "white" }}
            >
              Delete
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      {/* Device Tokens Info Tooltip */}
      {/* <Box sx={{ display: "flex", alignItems: "center", gap: "6px", mb: "14px" }}>
        <Typography
          sx={{
            
            fontSize: "13.5px",
            color: "#5C6B7A",
            mt:1
          }}
        >
          What is a device token?
        </Typography>
        <Tooltip
          title="A device token is created each time you log in from a new device or browser, and it lets us send push notifications to that specific device. Each device you've logged in from (e.g. work PC, laptop, phone) has its own separate token, even under the same account. If a token is removed, that device stops receiving notifications even if you're still logged in — you'll need to log in again on it to restore notifications. The most recently used token can't be removed, so at least one device always stays able to receive notifications."
          arrow
          placement="right"

          componentsProps={{
            tooltip: {
              sx: {
                bgcolor: "white",
                color: "#8A8C8F",
                boxShadow: 1,
                fontWeight: "400",
                border: "1px solid #a6a6a6",
                borderRadius: "12px",
                letterSpacing: "2%",
                fontSize: "12px",
                padding: "10px"
              },
            },
          }}
          PopperProps={{
            modifiers: [
              {
                name: "offset",
                options: { offset: [40, -10] },
              },
            ],
          }}
        >
          <InfoOutlinedIcon sx={{ color: "#8A97A5", fontSize: "16px", cursor: "pointer", alignSelf: "flex-end", mb: "1px" }} />
        </Tooltip>
      </Box> */}
      <Box sx={{mt:2}}>
        <DeviceTokenDesc />
      </Box>
      
<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
  {deviceTokens?.length > 0 ? (
    deviceTokens.map((item, index) => {
      const { icon, bg } = getPlatformIcon(item?.platform);
      const isActive = isLatestForPlatform(item);

      return (
        <Box
          key={item?.id || index}
          sx={{
            border: `1px solid ${colors.border.default}`,
            borderRadius: '10px',
            overflow: 'hidden',
            boxShadow:` 0px 1px 5px 0px ${colors.border.card}`
          }}
        >
          {/* Header: icon + device id */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 2 }}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                bgcolor: bg,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              {icon}
            </Box>
            <Box sx={{ overflow: 'hidden' }}>
              <Typography sx={{ fontWeight: 600, fontSize: '15px', textTransform: 'capitalize' }}>
                {item?.deviceName ?? item?.platform ?? '-'}
              </Typography>
              <Typography
                sx={{
                  fontSize: '13px',
                  color: colors.text.secondary,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {item?.deviceId ?? '-'}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ borderTop: `1px solid ${colors.border.default}` }} />

          {/* Created on + Token row */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              px: 2,
              py: 1.5,
              gap: 2,
            }}
          >
            <Box sx={{width:"50%"}}>
              <Box sx={{display:"flex", gap:2}}>
                <CreatedOnIcon />
                <Box sx={{display:"flex",flexDirection:"column",gap:'4px'}}>
                <Typography sx={{ fontSize: '11px', color: colors.text.secondary, letterSpacing: '0.5px' }}>
                  CREATED ON
                </Typography>
                <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
                  {item?.lastLoginAt
                    ? moment(item.lastLoginAt).format('Do MMMM YYYY, hh:mm A')
                    : '-'}
                </Typography>
                </Box>
              </Box>
            </Box>
            <Box sx={{ borderRight: `1px solid ${colors.border.default}` }} />
            <Box sx={{ textAlign: 'left', flex:1 }}>
              <Box sx={{display:"flex", gap:2}}>
                <TokenIcon />
                <Box sx={{display:"flex",flexDirection:"column",gap:"4px"}}>
                  <Typography sx={{ fontSize: '11px', color: colors.text.secondary, letterSpacing: '0.5px' }}>
                    TOKEN
                  </Typography>
                  <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
                    {item?.token ? maskToken(item.token) : '-'}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>

          <Box sx={{width:"100%", display:"flex",justifyContent:"center",mb:2, p:2}}>
            {/* Action button */}
            <Button
              fullWidth
              disableElevation
              disableRipple={isActive}
              onClick={!isActive ? () => handleDeleteClick(item) : undefined}
              sx={{
                borderRadius: '10px',
                width:"100%",
                height:"45px",
                py: 1.4,
                fontWeight: 600,
                fontSize: '14px',
                textTransform: 'none',
                color: '#fff',
                bgcolor: isActive ? '#6FCF77' : '#E15B5B',
                cursor: isActive ? 'default' : 'pointer',
                '&:hover': {
                  bgcolor: isActive ? '#6FCF77' : '#d64f4f',
                },
              }}
            >
              {isActive ? 'Active Token' : 'Delete Token'}
            </Button>
          </Box>
        </Box>
      );
    })
  ) : (
    <Typography sx={{ textAlign: 'center', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      No Device Tokens Found
    </Typography>
  )}
</Box>

    </>
  );
};

export default DeviceTokens;